/**
 * Notification Item
 *
 * Single notification in the panel list.
 * Shows severity icon, content, relative time, and hover actions.
 */

import { DateTime } from "luxon";
import {
  Check,
  X,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { AppNotification } from "@/stores/useNotificationStore";

interface NotificationItemProps {
  notification: AppNotification;
  onMarkRead: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigate?: (url: string) => void;
  onClick?: (notification: AppNotification) => void;
}

const severityIcons = {
  info: Info,
  success: CheckCircle,
  warning: AlertCircle,
  error: XCircle,
} as const;

const severityColors = {
  info: "text-blue-500",
  success: "text-emerald-500",
  warning: "text-amber-500",
  error: "text-red-500",
} as const;

const severityBorderColors = {
  info: "border-l-blue-400",
  success: "border-l-emerald-400",
  warning: "border-l-amber-400",
  error: "border-l-red-400",
} as const;

const severityBgColors = {
  info: "bg-blue-50/60 dark:bg-blue-950/20",
  success: "bg-emerald-50/60 dark:bg-emerald-950/20",
  warning: "bg-amber-50/60 dark:bg-amber-950/20",
  error: "bg-red-50/60 dark:bg-red-950/20",
} as const;

export function NotificationItem({
  notification,
  onMarkRead,
  onDelete,
  onNavigate,
  onClick,
}: NotificationItemProps) {
  const {
    id,
    title,
    message,
    severity,
    actionUrl,
    actionLabel,
    isRead,
    createdAt,
  } = notification;

  const Icon = severityIcons[severity];
  const timeAgo = DateTime.fromJSDate(createdAt).toRelative() ?? "";

  const handleClick = () => {
    if (!isRead) {
      onMarkRead(id);
    }
    onClick?.(notification);

    if (actionUrl && onNavigate) {
      onNavigate(actionUrl);
    }
  };

  const handleMarkRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkRead(id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(id);
  };

  return (
    <div
      role="listitem"
      data-testid={`notification-row-${id}`}
      data-read={isRead ? "true" : "false"}
      className={cn(
        "group relative flex gap-3 py-3 px-4 cursor-pointer",
        "border-l-[3px] border-l-transparent",
        "hover:bg-accent/30 transition-all duration-150",
        "border-b border-border/50 last:border-b-0",
        !isRead && [severityBorderColors[severity], severityBgColors[severity]],
      )}
      onClick={handleClick}
    >
      {/* Icon */}
      <div
        className={cn(
          "shrink-0 mt-0.5 flex items-center justify-center",
          "h-8 w-8 rounded-full",
          !isRead ? severityColors[severity] : "text-muted-foreground",
          !isRead && severityBgColors[severity],
        )}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 text-left">
        <p
          className={cn(
            "text-sm leading-tight text-left",
            !isRead
              ? "font-semibold text-foreground"
              : "font-medium text-muted-foreground",
          )}
        >
          {title}
        </p>
        <p className="text-[13px] text-muted-foreground line-clamp-2 mt-0.5 leading-snug text-left">
          {message}
        </p>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-xs text-muted-foreground/70">{timeAgo}</span>
          {actionUrl && actionLabel && (
            <span
              className={cn(
                "text-xs font-medium flex items-center gap-1",
                "text-primary hover:text-primary/80 transition-colors",
              )}
            >
              {actionLabel}
              <ExternalLink className="h-3 w-3" />
            </span>
          )}
        </div>
      </div>

      {/* Actions (visible on hover) */}
      <div
        className={cn(
          "shrink-0 flex items-center gap-0.5",
          "opacity-0 group-hover:opacity-100 transition-opacity duration-150",
        )}
      >
        {!isRead && (
          <Button
            variant="ghost"
            size="icon"
            data-testid="notification-mark-read-button"
            className="h-7 w-7 hover:bg-accent/30"
            style={{ borderRadius: "8px" }}
            onClick={handleMarkRead}
            aria-label="Mark as read"
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          data-testid="notification-dismiss-button"
          className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          style={{ borderRadius: "8px" }}
          onClick={handleDelete}
          aria-label="Delete notification"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
